import React from "react";
import { FaFileContract, FaGavel, FaHandshake, FaExclamationTriangle } from "react-icons/fa";
import SEO from "../../components/common/SEO";
import Breadcrumb from "../../components/common/Breadcrumb";

const Terms = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service - Arvix Creative",
    "description": "Terms of Service for Arvix Creative. Read our terms and conditions for using our creative services, including payment terms, intellectual property rights, and client responsibilities.",
    "url": "https://arvixcreative.com/terms",
    "datePublished": "2024-01-15",
    "dateModified": "2025-10-03",
    "publisher": {
      "@type": "Organization",
      "name": "Arvix Creative",
      "logo": {
        "@type": "ImageObject",
        "url": "https://arvixcreative.com/logo512.png"
      }
    }
  };

  return (
    <>
      <SEO 
        title="Terms of Service - Arvix Creative | Terms & Conditions"
        description="Terms of Service for Arvix Creative. Read our terms and conditions for using our creative services, including payment terms, intellectual property rights, and client responsibilities. Last updated: October 3, 2025."
        keywords="terms of service, terms and conditions, Arvix Creative terms, service agreement, design service terms, legal terms"
        url="https://arvixcreative.com/terms"
        structuredData={structuredData}
      />
      <div className="min-h-screen relative overflow-hidden">
      {/* Consistent Background */}
      
      
      <div className="relative z-10 w-full py-16 md:py-24">
        <Breadcrumb items={[{ label: "Terms of Service" }]} className="mb-8" />

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <FaFileContract className="text-4xl text-designer-primary" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance">
              Terms of Service
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Please read these terms carefully before using our services. By using our services, you agree to be bound by these terms.
          </p>
          <p className="text-sm text-gray-500 mt-2">Last updated: 3rd October 2025</p>
        </div>

        {/* Content */}
        <div>
          <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 shadow-red shadow-red-hover border border-gray-200">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4 flex items-center gap-3">
                <FaGavel className="text-designer-primary" />
                Agreement to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of Arvix Creative's website and services. 
                By accessing or using our services, you agree to be bound by these Terms. If you disagree 
                with any part of these terms, you may not access our services.
              </p>
            </section>

            {/* Services Description */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Our Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Arvix Creative provides professional creative services including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Brand identity design and development</li>
                <li>UI/UX design for web and mobile applications</li>
                <li>Printing and packaging for marketing materials</li>
                <li>Design consultation and strategy</li>
              </ul>
            </section>

            {/* Client Responsibilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4 flex items-center gap-3">
                <FaHandshake className="text-designer-primary" />
                Client Responsibilities
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As our client, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Provide accurate and complete project information</li>
                <li>Respond to requests for feedback in a timely manner</li>
                <li>Provide necessary materials and content for the project</li>
                <li>Respect intellectual property rights</li>
                <li>Make payments according to agreed terms</li>
                <li>Use delivered work only for its intended purpose</li>
              </ul>
            </section>

            {/* Payment Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Payment Terms</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-designer-dark mb-2">Payment Schedule</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Payment terms will be outlined in individual project agreements. Generally, we require:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2">
                    <li>50% deposit to begin work</li>
                    <li>Remaining balance upon project completion</li>
                    <li>Payment due within 30 days of invoice date</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-designer-dark mb-2">Late Payments</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Late payments may incur additional fees. We reserve the right to suspend work 
                    on outstanding projects until payment is received.
                  </p>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Intellectual Property</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-designer-dark mb-2">Client Ownership</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Upon full payment, clients receive ownership of final design deliverables. 
                    This includes final artwork, logos, and completed design files.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-designer-dark mb-2">Designer Rights</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We retain the right to use completed work in our project gallery and marketing materials 
                    unless otherwise specified in the project agreement.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-designer-dark mb-2">Third-Party Content</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Clients are responsible for ensuring they have rights to any content, images, 
                    or materials provided for use in the project.
                  </p>
                </div>
              </div>
            </section>

            {/* Revisions and Changes */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Revisions and Changes</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We include a reasonable number of revisions in our project scope. Additional revisions 
                may incur additional charges. Major changes to project scope may require a new agreement.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Minor revisions are typically included in the project cost</li>
                <li>Major changes may require additional time and cost</li>
                <li>Revision requests should be made within 30 days of delivery</li>
                <li>Changes to approved designs may incur additional charges</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4 flex items-center gap-3">
                <FaExclamationTriangle className="text-designer-primary" />
                Limitation of Liability
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our liability is limited to the total amount paid for the specific project. We are not 
                liable for any indirect, incidental, or consequential damages. We provide professional 
                services but cannot guarantee specific business outcomes or results.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Either party may terminate a project with written notice. Upon termination:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Client pays for work completed up to the termination date</li>
                <li>We deliver completed work to the client</li>
                <li>Intellectual property rights transfer as per the agreement</li>
                <li>Confidentiality obligations continue to apply</li>
              </ul>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of Bangladesh. Any disputes will be resolved 
                through binding arbitration in Chattogram, Bangladesh.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-designer-dark mb-4">Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify clients of 
                significant changes via email or through our website. Continued use of our services 
                after changes constitutes acceptance of the new Terms.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Terms;
